import { useState, useCallback } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";

// ============================================================
// Types matching the server's invoice-matcher output
// ============================================================

export type MatchClassification = "green" | "yellow" | "red" | "blue";

export interface MatchedLineItem {
  poLineItemId: string | null;
  classification: MatchClassification;
  vendorCode: string;
  sku: string | null;
  productTitle: string | null;
  variantTitle: string | null;
  quantityOrdered: number;
  quantityPrevReceived: number;
  packSize: number;
  caseCost: number;
  offInvoice: number;
  byCheque: number;
  freeGoods: number;
  vendorDiscountPct: number;
  netCaseCost: number;
  invoiceQty: number;
  invoiceCaseCost: number | null;
  invoiceDiscount: number;
  invoiceDescription: string;
  invoiceNumber: string;
  invoiceVendorCode: string;
  costChanged: boolean;
  newCaseCost: number | null;
}

export interface MatchSummary {
  totalPOItems: number;
  greenCount: number;
  yellowCount: number;
  redCount: number;
  blueCount: number;
  poAmount: number;
  receivedAmount: number;
  shortShippedAmount: number;
  outOfStockPct: number;
}

export interface UploadedInvoice {
  id: string;
  filename: string;
  fileSize: number;
  status: string;
  invoiceNumber?: string;
  errorMessage?: string;
}

export interface InvoiceMeta {
  invoiceNumber: string;
  invoiceDate: string | null;
}

export interface ProcessResult {
  matchedItems: MatchedLineItem[];
  summary: MatchSummary;
  invoiceMeta?: InvoiceMeta[];
}

export function useInvoiceProcessing() {
  const authFetch = useAuthenticatedFetch();
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedInvoices, setUploadedInvoices] = useState<UploadedInvoice[]>([]);
  const [processResult, setProcessResult] = useState<ProcessResult | null>(null);
  const [hasSavedResults, setHasSavedResults] = useState(false);

  const loadInvoices = useCallback(
    async (poId: string) => {
      try {
        const res = await authFetch(`/api/purchase-orders/${poId}/invoices`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        setUploadedInvoices(json.data || []);
      } catch (err: any) {
        setError(err.message);
      }
    },
    [authFetch]
  );

  const loadSavedResults = useCallback(
    async (poId: string): Promise<ProcessResult | null> => {
      setLoadingResults(true);
      try {
        const res = await authFetch(`/api/purchase-orders/${poId}/invoices/results`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);

        if (json.data) {
          const result: ProcessResult = {
            ...json.data,
            invoiceMeta: json.invoiceMeta || [],
          };
          setProcessResult(result);
          setHasSavedResults(true);
          return result;
        }
        return null;
      } catch (err: any) {
        // Don't show error for no results — that's normal
        return null;
      } finally {
        setLoadingResults(false);
      }
    },
    [authFetch]
  );

  const clearSavedResults = useCallback(
    async (poId: string): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${poId}/invoices/results`, {
          method: "DELETE",
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        setProcessResult(null);
        setHasSavedResults(false);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch]
  );

  const uploadInvoices = useCallback(
    async (poId: string, files: File[]): Promise<boolean> => {
      setUploading(true);
      setError(null);
      try {
        const formData = new FormData();
        for (const file of files) {
          formData.append("invoices", file);
        }

        const res = await authFetch(`/api/purchase-orders/${poId}/invoices/upload`, {
          method: "POST",
          body: formData,
          // Don't set Content-Type — browser will set it with boundary for multipart
        });

        const json = await res.json();
        if (!res.ok) throw new Error(json.error);

        // Refresh invoice list
        await loadInvoices(poId);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      } finally {
        setUploading(false);
      }
    },
    [authFetch, loadInvoices]
  );

  const processInvoices = useCallback(
    async (poId: string): Promise<ProcessResult | null> => {
      setProcessing(true);
      setError(null);
      setProcessResult(null);
      try {
        const res = await authFetch(`/api/purchase-orders/${poId}/invoices/process`, {
          method: "POST",
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);

        const result: ProcessResult = {
          ...json.data,
          invoiceMeta: json.invoiceMeta || [],
        };
        setProcessResult(result);
        setHasSavedResults(true);

        // Refresh invoice list to show updated statuses
        await loadInvoices(poId);

        return result;
      } catch (err: any) {
        setError(err.message);
        return null;
      } finally {
        setProcessing(false);
      }
    },
    [authFetch, loadInvoices]
  );

  const deleteInvoice = useCallback(
    async (poId: string, invoiceId: string): Promise<boolean> => {
      try {
        const res = await authFetch(`/api/purchase-orders/${poId}/invoices/${invoiceId}`, {
          method: "DELETE",
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error);
        await loadInvoices(poId);
        return true;
      } catch (err: any) {
        setError(err.message);
        return false;
      }
    },
    [authFetch, loadInvoices]
  );

  const clearResults = useCallback(() => {
    setProcessResult(null);
    setError(null);
  }, []);

  return {
    uploading,
    processing,
    loadingResults,
    error,
    uploadedInvoices,
    processResult,
    hasSavedResults,
    loadInvoices,
    loadSavedResults,
    uploadInvoices,
    processInvoices,
    deleteInvoice,
    clearResults,
    clearSavedResults,
  };
}
